import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';

import humanizeString from 'humanize-string';

import { CloseCircleOutline, useToast } from '@aslanpay/portal-common-lib';

import useInviteIndividuals from 'app/hooks/useInviteIndividuals';

import { InviteIndividualModalProps } from './types';

function InviteIndividualModal({
  selectedIndividuals,
  isOpen,
  onClose,
  onInvite,
  type,
}: InviteIndividualModalProps) {
  const toast = useToast();

  const title =
    selectedIndividuals.length > 1 ? `Invite the selected ${type}(s)?` : `Invite ${type}?`;
  const description = selectedIndividuals.length > 1 ? `the selected ${type}s` : `this ${type}`;
  const desccriptionAction =
    type === 'employee' ? 'download the Aslan app and register' : 'proceed with the register';

  const { mutate: inviteIndividuals, isLoading: isInvitingIndividuals } = useInviteIndividuals({
    onSuccess: () => {
      const toastDescription = `${selectedIndividuals.length} ${
        selectedIndividuals.length > 1 ? `${type}s were` : `${type} was`
      } successfully invited!`;

      toast({
        position: 'top-right',
        status: 'success',
        title: `${humanizeString(type)}s invited`,
        description: toastDescription,
      });

      onInvite();
      onClose();
    },
    onError: () => {
      const toastDescription =
        selectedIndividuals.length > 1 ? `not all ${type}s were` : `the ${type} was not`;

      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: `Sorry, ${toastDescription} invited. Please try again.`,
      });

      onClose();
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">{title}</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close create new admin"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text color="brand.mutedPurple" textStyle="body">
            {`We will send a welcome email to ${description}, with a link to ${desccriptionAction}.`}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Spacer />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            isLoading={isInvitingIndividuals}
            onClick={() => inviteIndividuals({ ids: selectedIndividuals })}
          >
            Yes, invite
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default InviteIndividualModal;
